import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchQuestions, submitAssessment } from "@/lib/api";
import { Header } from "@/components/Brand";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ArrowRight, Loader2, Mail } from "lucide-react";

export default function Assessment() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stepIdx, setStepIdx] = useState(0);
    const [answers, setAnswers] = useState({}); // { qid: int | string[] | { ...compound } | { selected: [], text: '' } }
    const [signals, setSignals] = useState({}); // { qid: signal_key } — captured from tagged options
    const [optIdxMap, setOptIdxMap] = useState({}); // { qid: optionIdx } — for highlighting selected radio
    const [lead, setLead] = useState({ name: "", email: "", business_name: "" });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchQuestions()
            .then((d) => setCategories(d.categories || []))
            .catch(() => toast.error("Couldn't load the assessment. Please refresh."))
            .finally(() => setLoading(false));
    }, []);

    // Scroll to top whenever the step changes — smooth on real navigation, instant on load
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [stepIdx]);

    const totalSteps = categories.length + 1;
    const isLeadStep = stepIdx === categories.length;
    const currentCategory = categories[stepIdx];

    const totalAnswerableQuestions = useMemo(() => {
        let n = 0;
        for (const c of categories) {
            for (const q of c.questions) {
                n += 1;
            }
        }
        return n;
    }, [categories]);

    const answeredCountUpTo = useMemo(() => {
        let n = 0;
        for (let i = 0; i < stepIdx && i < categories.length; i++) {
            n += categories[i].questions.length;
        }
        return n;
    }, [stepIdx, categories]);

    const isAnswered = (q) => {
        const v = answers[q.id];
        const t = q.type || "single";
        if (t === "single") return typeof v === "number";
        if (t === "compound") return v && q.fields.every((f) => v[f.id]);
        if (t === "multi") {
            const sel = v?.selected;
            return Array.isArray(sel) && (sel.length > 0 || (v?.text && v.text.trim() !== ""));
        }
        if (t === "open_text") return true; // always optional / always "answered"
        return false;
    };

    const progressPct = useMemo(() => {
        if (totalSteps === 0) return 0;
        if (isLeadStep) return 96;
        const denom = totalAnswerableQuestions || 1;
        const answeredInCurrent = currentCategory
            ? currentCategory.questions.filter(isAnswered).length
            : 0;
        const ans = answeredCountUpTo + answeredInCurrent;
        return Math.min(95, Math.round((ans / denom) * 95));
    }, [stepIdx, isLeadStep, totalAnswerableQuestions, answers, currentCategory, answeredCountUpTo, totalSteps]);

    const currentCategoryRequiredComplete = useMemo(() => {
        if (!currentCategory) return false;
        // Only "single" + "compound" are required. "multi" and "open_text" are optional.
        return currentCategory.questions.every((q) => {
            const t = q.type || "single";
            if (t === "multi" || t === "open_text") return true;
            return isAnswered(q);
        });
    }, [currentCategory, answers]);

    const goNext = () => {
        if (!isLeadStep && !currentCategoryRequiredComplete) {
            toast.error("Please answer all questions in this section.");
            return;
        }
        setStepIdx((s) => Math.min(s + 1, totalSteps - 1));
    };
    const goBack = () => {
        setStepIdx((s) => Math.max(s - 1, 0));
    };

    // Setters
    const setSingle = (qid, score, optIdx, signal) => {
        setAnswers((p) => ({ ...p, [qid]: score }));
        setOptIdxMap((p) => ({ ...p, [qid]: optIdx }));
        setSignals((p) => {
            const next = { ...p };
            if (signal) next[qid] = signal;
            else delete next[qid];
            return next;
        });
    };
    const setCompoundField = (qid, fieldId, value) => {
        setAnswers((p) => ({ ...p, [qid]: { ...(p[qid] || {}), [fieldId]: value } }));
    };
    const toggleMulti = (qid, label) => {
        setAnswers((p) => {
            const cur = p[qid] || { selected: [], text: "" };
            const selected = Array.isArray(cur.selected) ? [...cur.selected] : [];
            const i = selected.indexOf(label);
            if (i >= 0) selected.splice(i, 1);
            else selected.push(label);
            return { ...p, [qid]: { ...cur, selected } };
        });
    };
    const setMultiText = (qid, text) => {
        setAnswers((p) => {
            const cur = p[qid] || { selected: [], text: "" };
            return { ...p, [qid]: { ...cur, text } };
        });
    };
    const setOpenText = (qid, text) => {
        setAnswers((p) => ({ ...p, [qid]: text }));
    };

    const handleSubmit = async () => {
        if (!lead.name.trim() || !lead.email.trim()) {
            toast.error("Please share your name and email so we can send your report.");
            return;
        }
        setSubmitting(true);
        try {
            const data = await submitAssessment({ ...lead, answers, signals });
            toast.success("Your report is ready!");
            navigate(`/report/${data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-black animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white" data-testid="assessment-page">
            <Header />

            <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-6 pb-24">
                <div className="mb-10" data-testid="progress-wrapper">
                    <div className="flex items-center justify-between mb-2">
                        <p className="overline">
                            {isLeadStep ? "Final step" : `Part ${stepIdx + 1} of ${categories.length}`}
                        </p>
                        <p className="text-sm text-black/60">{progressPct}% complete</p>
                    </div>
                    <Progress value={progressPct} className="h-2 bg-black/10 [&>div]:bg-pm-yellow" />
                </div>

                {!isLeadStep && currentCategory && (
                    <div key={currentCategory.id} className="fade-up" data-testid={`category-${currentCategory.id}`}>
                        <h2 className="font-display uppercase text-4xl sm:text-5xl text-black leading-[0.95]">
                            {currentCategory.name}
                        </h2>
                        <p className="mt-4 text-black/75 text-lg">{currentCategory.description}</p>

                        <div className="mt-10 space-y-6">
                            {currentCategory.questions.map((q, qi) => (
                                <QuestionCard
                                    key={q.id}
                                    q={q}
                                    qi={qi}
                                    answers={answers}
                                    optIdxMap={optIdxMap}
                                    setSingle={setSingle}
                                    setCompoundField={setCompoundField}
                                    toggleMulti={toggleMulti}
                                    setMultiText={setMultiText}
                                    setOpenText={setOpenText}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {isLeadStep && (
                    <div className="fade-up" data-testid="lead-capture-step">
                        <p className="overline mb-2 text-pm-yellow-deep">Almost there</p>
                        <h2 className="font-display uppercase text-4xl sm:text-5xl text-black leading-[0.95]">
                            Where should we<br /><span className="bg-pm-yellow px-2 inline-block">send your report?</span>
                        </h2>
                        <p className="mt-4 text-black/75 text-lg max-w-xl">
                            Free, no spam. Your personalised <strong>Profitable-But-Broke Reality Check</strong> lands
                            in your inbox right after you click submit.
                        </p>

                        <div className="mt-10 pm-card p-6 sm:p-10 space-y-6">
                            <div>
                                <label className="overline">Your name</label>
                                <Input
                                    data-testid="lead-name"
                                    value={lead.name}
                                    onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))}
                                    className="mt-2 bg-transparent border-0 border-b-2 border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black px-0 py-3 text-lg h-auto"
                                    placeholder="Magdalena Smith"
                                />
                            </div>
                            <div>
                                <label className="overline">Email</label>
                                <Input
                                    data-testid="lead-email"
                                    type="email"
                                    value={lead.email}
                                    onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
                                    className="mt-2 bg-transparent border-0 border-b-2 border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black px-0 py-3 text-lg h-auto"
                                    placeholder="you@yourbusiness.co.uk"
                                />
                            </div>
                            <div>
                                <label className="overline">Business name <span className="text-black/50 normal-case tracking-normal">(optional)</span></label>
                                <Input
                                    data-testid="lead-business"
                                    value={lead.business_name}
                                    onChange={(e) => setLead((p) => ({ ...p, business_name: e.target.value }))}
                                    className="mt-2 bg-transparent border-0 border-b-2 border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black px-0 py-3 text-lg h-auto"
                                    placeholder="Your business"
                                />
                            </div>
                            <p className="text-xs text-black/60 flex items-start gap-2 pt-2">
                                <Mail className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>
                                    We'll only use this to send you your personalised report and the occasional,
                                    useful insight from Magdalena @ProfitMIND. Unsubscribe anytime. For more
                                    information on how we use your data, please see our{" "}
                                    <a href="https://profitmind.co.uk/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                                        Privacy Policy
                                    </a>.
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                <div className="mt-10 flex items-center justify-between" data-testid="step-nav">
                    <button
                        onClick={goBack}
                        disabled={stepIdx === 0}
                        data-testid="btn-back"
                        className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-black/70 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>

                    {!isLeadStep ? (
                        <button onClick={goNext} data-testid="btn-next" className="btn-pm text-sm">
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button onClick={handleSubmit} disabled={submitting} data-testid="btn-submit" className="btn-pm text-sm disabled:opacity-60">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                            {submitting ? "Generating your Reality Check…" : "See my Reality Check"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function QuestionCard({ q, qi, answers, optIdxMap, setSingle, setCompoundField, toggleMulti, setMultiText, setOpenText }) {
    const t = q.type || "single";

    if (t === "open_text") {
        const v = answers[q.id] || "";
        return (
            <fieldset data-testid={`question-${q.id}`} className="pm-card p-6 sm:p-8">
                <legend className="font-display uppercase text-xl sm:text-2xl text-black mb-1 leading-snug">
                    <span className="text-pm-yellow-deep mr-2">Q{qi + 1}.</span>
                    {q.text}
                </legend>
                {q.subtext && <p className="text-sm text-black/60 mb-4">{q.subtext}</p>}
                <textarea
                    data-testid={`open-text-${q.id}`}
                    value={v}
                    onChange={(e) => setOpenText(q.id, e.target.value)}
                    placeholder={q.placeholder || "Optional — share whatever feels true."}
                    rows={5}
                    className="mt-2 w-full bg-white border-2 border-black/15 hover:border-black focus:border-black focus:ring-0 rounded-xl px-4 py-3 text-base text-black resize-y"
                />
            </fieldset>
        );
    }

    if (t === "compound") {
        const v = answers[q.id] || {};
        return (
            <fieldset data-testid={`question-${q.id}`} className="pm-card p-6 sm:p-8">
                <legend className="font-display uppercase text-xl sm:text-2xl text-black mb-1 leading-snug">
                    <span className="text-pm-yellow-deep mr-2">Q{qi + 1}.</span>
                    {q.text}
                </legend>
                {q.subtext && <p className="text-sm text-black/60 mb-4">{q.subtext}</p>}
                <div className="grid sm:grid-cols-2 gap-5">
                    {q.fields.map((f) => (
                        <div key={f.id}>
                            <label className="overline">{f.label}</label>
                            <select
                                data-testid={`compound-${q.id}-${f.id}`}
                                value={v[f.id] || ""}
                                onChange={(e) => setCompoundField(q.id, f.id, e.target.value)}
                                className="mt-2 w-full bg-white border-2 border-black/15 hover:border-black focus:border-black focus:ring-0 rounded-xl px-4 py-3 text-base text-black"
                            >
                                <option value="">Choose…</option>
                                {f.options.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                            {f.text_trigger && v[f.id] === f.text_trigger && (
                                <Input
                                    data-testid={`compound-text-${q.id}-${f.id}`}
                                    value={v[`${f.id}_other`] || ""}
                                    onChange={(e) => setCompoundField(q.id, `${f.id}_other`, e.target.value)}
                                    placeholder={f.text_placeholder || "Tell us more…"}
                                    className="mt-3 bg-transparent border-0 border-b-2 border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black px-0 py-2 h-auto"
                                />
                            )}
                        </div>
                    ))}
                </div>
            </fieldset>
        );
    }

    if (t === "multi") {
        const v = answers[q.id] || { selected: [], text: "" };
        return (
            <fieldset data-testid={`question-${q.id}`} className="pm-card p-6 sm:p-8">
                <legend className="font-display uppercase text-xl sm:text-2xl text-black mb-1 leading-snug">
                    <span className="text-pm-yellow-deep mr-2">Q{qi + 1}.</span>
                    {q.text}
                </legend>
                {q.subtext && <p className="text-sm text-black/60 mb-4">{q.subtext}</p>}
                <div className="grid gap-3">
                    {q.options.map((opt, oi) => {
                        const selected = (v.selected || []).includes(opt.label);
                        return (
                            <button
                                key={oi}
                                type="button"
                                data-testid={`option-${q.id}-${oi}`}
                                onClick={() => toggleMulti(q.id, opt.label)}
                                className={`text-left rounded-xl border-2 px-5 py-4 transition-all flex items-start gap-3 ${
                                    selected ? "border-black bg-pm-yellow" : "border-black/15 hover:border-black bg-white"
                                }`}
                            >
                                <span className={`inline-flex w-5 h-5 rounded-md border-2 items-center justify-center flex-shrink-0 mt-0.5 ${selected ? "border-black bg-black" : "border-black/40"}`}>
                                    {selected && <span className="text-pm-yellow text-xs font-black">✓</span>}
                                </span>
                                <span className="text-black">{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
                {q.allow_text && (
                    <Input
                        data-testid={`multi-text-${q.id}`}
                        value={v.text || ""}
                        onChange={(e) => setMultiText(q.id, e.target.value)}
                        placeholder={q.text_placeholder || "Anything else?"}
                        className="mt-4 bg-transparent border-0 border-b-2 border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black px-0 py-3 h-auto"
                    />
                )}
            </fieldset>
        );
    }

    // single
    return (
        <fieldset data-testid={`question-${q.id}`} className="pm-card p-6 sm:p-8">
            <legend className="font-display uppercase text-xl sm:text-2xl text-black mb-1 leading-snug">
                <span className="text-pm-yellow-deep mr-2">Q{qi + 1}.</span>
                {q.text}
            </legend>
            {q.subtext && <p className="text-sm text-black/60 mb-4">{q.subtext}</p>}
            <div className="grid gap-3 mt-3">
                {q.options.map((opt, oi) => {
                    const isSel = optIdxMap[q.id] === oi;
                    return (
                        <button
                            key={oi}
                            type="button"
                            data-testid={`option-${q.id}-${oi}`}
                            onClick={() => setSingle(q.id, opt.score, oi, opt.signal)}
                            className={`text-left rounded-xl border-2 px-5 py-4 transition-all flex items-center gap-3 ${
                                isSel ? "border-black bg-pm-yellow" : "border-black/15 hover:border-black bg-white"
                            }`}
                        >
                            <span className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center flex-shrink-0 ${isSel ? "border-black" : "border-black/40"}`}>
                                {isSel && <span className="w-2.5 h-2.5 rounded-full bg-black" />}
                            </span>
                            <span className="text-black">{opt.label}</span>
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
}
